import { z } from 'zod'

/**
 * The rules every directory's search params obey, kept apart from any one directory's fields. A
 * directory's own `search.ts` supplies the filters, the sort values and the mapping onto its
 * API; this supplies what the term, the sort and the page mean wherever they appear.
 */

/** The API ignores a one-character `q`, so no directory spends a request on one. */
export const MIN_QUERY_LENGTH = 2

/** Long enough that typing a stock number or a surname is one request, short enough to feel immediate. */
export const SEARCH_DEBOUNCE_MS = 300

/** The term the API is asked for: the URL's, unless it is shorter than the API reads. */
export function queryTerm(search: { q?: string | undefined }): string | undefined {
  return search.q && search.q.length >= MIN_QUERY_LENGTH ? search.q : undefined
}

/** The term, as every schema carries it: trimmed, bounded, absent when blank. */
export const termField = z
  .string()
  .max(100)
  .transform((value) => value.trim() || undefined)
  .optional()
  .catch(undefined)

/** The page, one-based because it is read by a person in the address bar. The API takes an offset. */
export const pageField = z.coerce.number().int().min(1).optional().catch(undefined)

/**
 * What every directory's search has in common. A directory's schema adds its own filters; every
 * other key it parses is one.
 */
export type DirectorySearch = {
  q?: string | undefined
  sort?: string | undefined
  page?: number | undefined
}

/** The sort values one directory's schema admits: `VehicleSort` from `VehicleListSearch`. */
export type SortOf<Search extends DirectorySearch> = NonNullable<Search['sort']>

/** A directory's search with its filters, whatever they are called. */
type AnySearch = DirectorySearch & { [filter: string]: unknown }

/** The keys that are not filters. */
const NOT_A_FILTER = new Set<string>(['q', 'sort', 'page'])

/** True when anything but the term, the sort and the page narrows the list. */
export function hasActiveFilters(search: AnySearch): boolean {
  return Object.entries(search).some(
    ([key, value]) => !NOT_A_FILTER.has(key) && value !== undefined,
  )
}

/**
 * True when "Clear filters" has something to clear: a filter, or a sort other than the default.
 * The term is its own control and is not part of this.
 */
export function isFiltered(search: AnySearch): boolean {
  return hasActiveFilters(search) || search.sort !== undefined
}

/**
 * The search params with every filter, the sort and the page removed; the term stays.
 *
 * Takes the one key it reads rather than a whole schema, because the router hands a callback the
 * union of every route's search params and only `q` is common to them.
 */
export function withoutFilters(search: { q?: string | undefined }): { q?: string | undefined } {
  return search.q ? { q: search.q } : {}
}

/** `-year` -> `year`; `year` -> dropped. Every sortable field, read off the values that sort descending. */
export type SortField<Sort extends string> = Sort extends `-${infer Field}` ? Field : never

/** How one directory sorts: the API's default, which fields start descending, and the words for the foot. */
export type SortRule<Sort extends string> = {
  /** As the API defaults. Never written to the URL: `nextSort` maps it to `undefined`. */
  default: Sort
  /** Fields whose first tap sorts descending: dates, because newest first is what anyone sorting by one wants. */
  descendingFirst: readonly SortField<Sort>[]
  /** What the table's foot says the rows are ordered by: `Sorted by date added`. */
  words: Record<SortField<Sort>, string>
}

/** `-year` -> `{ field: 'year', descending: true }`. No sort is the rule's default. */
export function parseSort<Sort extends string>(
  sort: Sort | undefined,
  rule: SortRule<Sort>,
): { field: SortField<Sort>; descending: boolean } {
  const value = sort ?? rule.default
  const descending = value.startsWith('-')
  return { field: (descending ? value.slice(1) : value) as SortField<Sort>, descending }
}

/**
 * The sort a header tap produces: a new field sorts ascending, or descending when the rule says
 * it starts that way; the same field again flips the direction. The default sort comes back as
 * `undefined`, so it never reaches the URL.
 */
export function nextSort<Sort extends string>(
  current: Sort | undefined,
  field: SortField<Sort>,
  rule: SortRule<Sort>,
): Sort | undefined {
  const { field: currentField, descending } = parseSort(current, rule)
  const wantDescending = currentField === field ? !descending : rule.descendingFirst.includes(field)
  const sort = (wantDescending ? `-${field}` : field) as Sort
  return sort === rule.default ? undefined : sort
}
